import { useCallback, useEffect, useMemo, useRef } from "react";
import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Flex, Menu, Portal, Spinner, Tabs, Text, VStack } from "@chakra-ui/react";
import { Badge, Button } from "@digital-services-gard-as/anchor-react";
import { BellIcon } from "@digital-services-gard-as/anchor-icons";
import { useTranslation } from "react-i18next";

import {
    allNotificationsQueryKey,
    getNotificationsInfiniteQueryOptions,
    getUnreadNotificationsQueryOptions,
    markNotificationAsReadMutationOptions,
    unreadNotificationsQueryKey
} from "@/api/queryOptions/notificationApiQueryOptions";
import { NormalizedSearchResultDto } from "@/api/claimsInvoicingApi";
import { NoResults } from "@/components/NoResult";
import { InAppNotificationDto } from "@/types";
import { StatusBadge } from "@/utils/invoiceUtils";
import { formatNotificationDateTime, getNotificationMessage } from "@/utils/notificationUtils";
import { Link } from "react-router-dom";

const MAX_BADGE_DISPLAY_COUNT = 99;
const SCROLL_THRESHOLD_PX = 100;

type FilterTab = "all" | "unread";

const markNotificationReadInPages = (
    current: InfiniteData<NormalizedSearchResultDto<InAppNotificationDto>> | undefined,
    notificationId: string
): InfiniteData<NormalizedSearchResultDto<InAppNotificationDto>> | undefined => {
    if (!current) {
        return current;
    }

    return {
        ...current,
        pages: current.pages.map(page => ({
            ...page,
            items: page.items.map(notification =>
                notification.id === notificationId ? { ...notification, isRead: true } : notification
            )
        }))
    };
};

export const Notification = () => {
    const { t } = useTranslation();

    const {
        data: unreadNotifications = [],
        isLoading: isUnreadLoading,
        isError: isUnreadError
    } = useQuery(getUnreadNotificationsQueryOptions());

    const {
        data: allNotificationsResult,
        isLoading: isAllLoading,
        isError: isAllError,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery(getNotificationsInfiniteQueryOptions());

    const allNotifications = useMemo(
        () => allNotificationsResult?.pages.flatMap(page => page.items ?? []) ?? [],
        [allNotificationsResult?.pages]
    );

    const unreadCount = unreadNotifications.length;
    const unreadBadgeValue = unreadCount > MAX_BADGE_DISPLAY_COUNT ? "99+" : String(unreadCount);

    return (
        <Menu.Root>
            <Menu.Trigger asChild>
                <Button
                    variant="tertiary"
                    icon={<BellIcon />}
                    aria-label={t("notifications.buttonAriaLabel")}
                    data-testid="notifications-trigger"
                    style={{
                        position: "relative",
                        minWidth: "2rem",
                        width: "2rem",
                        height: "2rem",
                        padding: "0.25rem",
                        fontSize: "1.5rem"
                    }}
                >
                    {unreadCount > 0 && (
                        <Box position="absolute" top="-0.5" right="-0.5" data-testid="notificationsUnreadBadge">
                            <Badge
                                variant="danger"
                                size="sm"
                                style={{
                                    minWidth: "1rem",
                                    minHeight: "1rem",
                                    padding: "0 0rem",
                                    fontWeight: "bold"
                                }}
                            >
                                {unreadBadgeValue}
                            </Badge>
                        </Box>
                    )}
                </Button>
            </Menu.Trigger>
            <NotificationTabs
                allNotifications={allNotifications}
                unreadNotifications={unreadNotifications}
                unreadCount={unreadCount}
                isAllLoading={isAllLoading}
                isUnreadLoading={isUnreadLoading}
                isAllError={isAllError}
                isUnreadError={isUnreadError}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={Boolean(hasNextPage)}
                fetchNextPage={fetchNextPage}
            />
        </Menu.Root>
    );
};

type NotificationTabsProps = {
    allNotifications: InAppNotificationDto[];
    unreadNotifications: InAppNotificationDto[];
    unreadCount: number;
    isAllLoading: boolean;
    isUnreadLoading: boolean;
    isAllError: boolean;
    isUnreadError: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
};

const NotificationTabs = ({
    allNotifications,
    unreadNotifications,
    unreadCount,
    isAllLoading,
    isUnreadLoading,
    isAllError,
    isUnreadError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
}: NotificationTabsProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { mutate: markAsRead } = useMutation({
        ...markNotificationAsReadMutationOptions,
        onMutate: async notificationId => {
            await queryClient.cancelQueries({ queryKey: unreadNotificationsQueryKey });
            await queryClient.cancelQueries({ queryKey: allNotificationsQueryKey });

            const previousUnread = queryClient.getQueryData<InAppNotificationDto[]>(unreadNotificationsQueryKey);
            const previousAll =
                queryClient.getQueryData<InfiniteData<NormalizedSearchResultDto<InAppNotificationDto>>>(
                    allNotificationsQueryKey
                );

            queryClient.setQueryData<InAppNotificationDto[]>(unreadNotificationsQueryKey, current =>
                current?.filter(notification => notification.id !== notificationId)
            );

            queryClient.setQueryData<InfiniteData<NormalizedSearchResultDto<InAppNotificationDto>>>(
                allNotificationsQueryKey,
                current => markNotificationReadInPages(current, notificationId)
            );

            return { previousUnread, previousAll };
        },
        onError: (_error, _notificationId, context) => {
            if (context?.previousUnread) {
                queryClient.setQueryData(unreadNotificationsQueryKey, context.previousUnread);
            }
            if (context?.previousAll) {
                queryClient.setQueryData(allNotificationsQueryKey, context.previousAll);
            }
        }
    });

    const handleNotificationClick = useCallback(
        (notificationId: string, isRead: boolean) => {
            if (isRead) {
                return;
            }

            markAsRead(notificationId);
        },
        [markAsRead]
    );

    const renderTabContent = (tab: FilterTab) => {
        const isAllTab = tab === "all";

        return (
            <NotificationTabContent
                isLoading={isAllTab ? isAllLoading : isUnreadLoading}
                isError={isAllTab ? isAllError : isUnreadError}
                notifications={isAllTab ? allNotifications : unreadNotifications}
                onNotificationClick={handleNotificationClick}
                hasNextPage={isAllTab ? hasNextPage : false}
                isFetchingNextPage={isAllTab ? isFetchingNextPage : false}
                onLoadMore={isAllTab ? fetchNextPage : undefined}
            />
        );
    };

    return (
        <Portal>
            <Menu.Positioner>
                <Menu.Content w={{ base: "calc(100vw - 2rem)", md: "513px" }} maxW="513px" p="0" overflow="hidden">
                    <Box pt="5" borderBottom="1px solid" borderColor="gray.200">
                        <Tabs.Root lazyMount defaultValue="all" css={{ "--indicator-color": "var(--a-navy-500)" }}>
                            <Tabs.List gap="6" justifyContent="flex-start" border="0" px="6" bg="transparent">
                                <Tabs.Trigger
                                    value="all"
                                    data-testid="notifications-tab-all"
                                    css={{ _focusVisible: { outline: "none", boxShadow: "none" } }}
                                >
                                    {t("notifications.all")}
                                </Tabs.Trigger>

                                <Tabs.Trigger
                                    value="unread"
                                    data-testid="notifications-tab-unread"
                                    css={{ _focusVisible: { outline: "none", boxShadow: "none" } }}
                                >
                                    {t("notifications.unreadWithCount", { count: unreadCount })}
                                </Tabs.Trigger>
                            </Tabs.List>

                            <Tabs.Content value="all" p="0">
                                {renderTabContent("all")}
                            </Tabs.Content>

                            <Tabs.Content value="unread" p="0">
                                {renderTabContent("unread")}
                            </Tabs.Content>
                        </Tabs.Root>
                    </Box>
                </Menu.Content>
            </Menu.Positioner>
        </Portal>
    );
};

type NotificationTabContentProps = {
    isLoading: boolean;
    isError: boolean;
    notifications: InAppNotificationDto[];
    onNotificationClick: (notificationId: string, isRead: boolean) => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore?: () => void;
};

const NotificationTabContent = ({
    isLoading,
    isError,
    notifications,
    onNotificationClick,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore
}: NotificationTabContentProps) => {
    const { t } = useTranslation();
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = listRef.current;

        if (!el || !onLoadMore) {
            return undefined;
        }

        const onScroll = () => {
            if (
                el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX &&
                hasNextPage &&
                !isFetchingNextPage &&
                !isLoading
            ) {
                onLoadMore();
            }
        };

        el.addEventListener("scroll", onScroll);

        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, [hasNextPage, isFetchingNextPage, isLoading, onLoadMore]);

    return (
        <Box
            ref={listRef}
            maxH="30rem"
            overflowY="auto"
            data-testid="notifications-list"
            px="2"
            borderTop="1px solid"
            borderColor="gray.200"
        >
            {isLoading && (
                <Flex justifyContent="center" p="6" data-testid="notifications-loading">
                    <Spinner size="sm" />
                </Flex>
            )}

            {!isLoading && isError && (
                <Box p="4">
                    <Text fontSize="sm">{t("notifications.error")}</Text>
                </Box>
            )}

            {!isLoading && !isError && notifications.length === 0 && (
                <Box p="4">
                    <Flex direction="column" alignItems="center" justifyContent="center">
                        <NoResults messages={[t("notifications.empty")]} />
                    </Flex>
                </Box>
            )}

            {!isLoading &&
                !isError &&
                notifications.map(notification => (
                    <NotificationListItem
                        key={notification.id}
                        notification={notification}
                        onClick={onNotificationClick}
                    />
                ))}

            {isFetchingNextPage && (
                <Flex justifyContent="center" p="4" data-testid="notifications-loading-more">
                    <Spinner size="sm" />
                </Flex>
            )}
        </Box>
    );
};

type NotificationListItemProps = {
    notification: InAppNotificationDto;
    onClick: (notificationId: string, isRead: boolean) => void;
};

const NotificationListItem = ({ notification, onClick }: NotificationListItemProps) => {
    const { t } = useTranslation();
    const notificationMessage = getNotificationMessage(notification.status, t);
    const formattedCreatedAt = formatNotificationDateTime(notification.createdAt);
    const invoicePath = `/invoice/${notification.invoiceId}`;
    const handleLinkClick = useCallback(() => {
        onClick(notification.id, notification.isRead);
    }, [notification.id, notification.isRead, onClick]);

    return (
        <Flex
            p="4"
            borderBottom="1px solid"
            borderColor="gray.200"
            alignItems="start"
            justifyContent="space-between"
            gap="3"
            data-testid={`notification-item-${notification.id}`}
        >
            <VStack align="start" gap="2" flex="1" minW="0">
                <Link
                    to={invoicePath}
                    onClick={handleLinkClick}
                    data-testid={`notification-link-${notification.id}`}
                    style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        color: "var(--chakra-colors-anchor-navy-900)",
                        textDecoration: "underline"
                    }}
                >
                    {t("notifications.invoiceReference", { reference: notification.invoiceReference })}
                </Link>
                <Text color="gray.600" fontSize="sm">
                    Claim: {notification.claimTitle} | Sub-claim: {notification.subClaimTitle}
                </Text>
                <StatusBadge
                    id={`notification-status-${notification.id}`}
                    invoiceStatus={notification.status}
                    size="sm"
                />
                {notificationMessage && <Text color="gray.800">{notificationMessage}</Text>}
            </VStack>

            <Flex
                direction="column"
                alignItems="flex-end"
                justifyContent="space-between"
                alignSelf="stretch"
                minW="1rem"
                pt="2"
                pr="1"
            >
                {!notification.isRead ? (
                    <Box
                        w="0.65rem"
                        h="0.65rem"
                        borderRadius="full"
                        bg="#005EA8"
                        data-testid="notification-unread-dot"
                    />
                ) : (
                    <Box />
                )}
                {formattedCreatedAt && (
                    <Text
                        color="gray.500"
                        fontSize="xs"
                        whiteSpace="nowrap"
                        data-testid={`notification-created-at-${notification.id}`}
                    >
                        {formattedCreatedAt}
                    </Text>
                )}
            </Flex>
        </Flex>
    );
};
